import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, Filter } from "lucide-react";
import { downloadCsv } from "@/lib/download";

interface ExportFilters {
  search?: string;
  tagId?: number;
  contactType?: string;
  state?: string;
  city?: string;
  organ?: string;
  sector?: string;
  jobTitle?: string;
  optedOut?: boolean;
}

export default function ExportContactsTab() {
  const [filters, setFilters] = useState<ExportFilters>({});
  const [exporting, setExporting] = useState(false);

  const { data: tagsList = [] } = trpc.tags.list.useQuery();
  const utils = trpc.useUtils();

  // Conta quantos contatos serão exportados (preview)
  const previewQuery = trpc.contacts.list.useQuery({
    ...filters,
    limit: 1,
    offset: 0,
  });
  const total = previewQuery.data?.total ?? 0;

  const exportMut = trpc.contacts.exportContacts.useMutation({
    onSuccess: (res) => {
      downloadCsv(res.csv, res.fileName);
      toast.success(`${res.total} contatos exportados — lote #${res.batchId}`);
      utils.contacts.importsList.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao exportar contatos")),
    onSettled: () => setExporting(false),
  });

  const handleExport = () => {
    setExporting(true);
    exportMut.mutate(Object.keys(filters).length > 0 ? filters : undefined);
  };

  const clearFilters = () => setFilters({});

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(v => v !== undefined && v !== "").length,
    [filters],
  );

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              Exportar contatos
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Gere um arquivo CSV dos contatos que correspondem aos filtros abaixo. A exportação fica registrada no histórico.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Busca (nome, telefone, e-mail)</Label>
              <Input
                placeholder="Digite para filtrar..."
                value={filters.search ?? ""}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value || undefined }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Tag</Label>
              <Select
                value={filters.tagId ? String(filters.tagId) : "all"}
                onValueChange={(v) => setFilters(f => ({ ...f, tagId: v === "all" ? undefined : Number(v) }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as tags</SelectItem>
                  {(tagsList as any[]).map((t: any) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Tipo de contato</Label>
              <Select
                value={filters.contactType ?? "all"}
                onValueChange={(v) => setFilters(f => ({ ...f, contactType: v === "all" ? undefined : v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="public_admin">Administração Pública</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                  <SelectItem value="cliente_final">Cliente Final</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">UF</Label>
              <Input
                placeholder="Ex: SP"
                maxLength={2}
                value={filters.state ?? ""}
                onChange={(e) => setFilters(f => ({ ...f, state: e.target.value.toUpperCase() || undefined }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Cidade</Label>
              <Input
                placeholder="Ex: São Paulo"
                value={filters.city ?? ""}
                onChange={(e) => setFilters(f => ({ ...f, city: e.target.value || undefined }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Órgão / Empresa</Label>
              <Input
                placeholder="Filtrar por órgão"
                value={filters.organ ?? ""}
                onChange={(e) => setFilters(f => ({ ...f, organ: e.target.value || undefined }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Setor</Label>
              <Input
                placeholder="Filtrar por setor"
                value={filters.sector ?? ""}
                onChange={(e) => setFilters(f => ({ ...f, sector: e.target.value || undefined }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Cargo</Label>
              <Input
                placeholder="Filtrar por cargo"
                value={filters.jobTitle ?? ""}
                onChange={(e) => setFilters(f => ({ ...f, jobTitle: e.target.value || undefined }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Opt-out</Label>
              <Select
                value={filters.optedOut === undefined ? "all" : filters.optedOut ? "yes" : "no"}
                onValueChange={(v) => setFilters(f => ({ ...f, optedOut: v === "all" ? undefined : v === "yes" }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="no">Apenas ativos</SelectItem>
                  <SelectItem value="yes">Apenas opt-out</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2 text-sm">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {activeFilterCount === 0 ? "Sem filtros — exporta todos" : `${activeFilterCount} filtro(s) ativo(s)`}
              </span>
              <Badge variant="secondary" className="text-xs">
                {previewQuery.isLoading ? "..." : `${total} contatos`}
              </Badge>
              {activeFilterCount > 0 && (
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={clearFilters}>
                  Limpar
                </Button>
              )}
            </div>

            <Button
              onClick={handleExport}
              disabled={exporting || total === 0}
              size="lg"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? "Exportando..." : `Exportar CSV`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground space-y-1">
          <p><strong>Formato:</strong> CSV com separador <code>;</code> e codificação UTF-8 (compatível com Excel/Google Sheets).</p>
          <p><strong>Privacidade:</strong> Campos sensíveis (e-mail, anotações, órgão, setor) são exportados em texto claro. Trate o arquivo com cuidado.</p>
        </CardContent>
      </Card>
    </div>
  );
}
